'use client';

import { Brand, CardId, Divider, Text } from '@stone-payments/jade';
import React from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { ApplePayLogoPlainIllustration } from '../../applePay/components/applePayLogoPlain.illustration';
import { ClickToPayLogoNoBrandIllustration } from '../../clickToPay';
import { GooglePayLogoPlainIllustration } from '../../googlePay/components/googlePayLogoPlain.illustration';
import { PAYMENT_METHODS_WITH_INSTALLMENTS } from './constants';
import {
  formatCustomerName,
  formatDocumentMasked,
  formatInstallment,
  formatPaymentMethodName,
} from './formatters';
import type {
  DigitalWalletPaymentInfo,
  PaymentInfo,
  PaymentInfoDisplayProps,
} from './types';

interface PaymentField {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

const WALLET_LOGOS: Record<
  Exclude<DigitalWalletPaymentInfo['walletType'], 'click_to_pay'>,
  React.ReactNode
> = {
  apple_pay: <ApplePayLogoPlainIllustration />,
  google_pay: <GooglePayLogoPlainIllustration />,
};

const CONTAINER_BASE = 'flex flex-col p-jade-150';
const CONTAINER_CARD =
  'rounded-jade-medium bg-jade-background-surface border border-jade-border-low';

function getPaymentMethodIcon(
  payment: PaymentInfo
): React.ReactNode | undefined {
  if (payment.paymentMethod === 'credit_card') {
    return <Brand size="large" use={payment.brand.toLowerCase() as CardId} />;
  }

  if (payment.paymentMethod !== 'digital_wallet') {
    return undefined;
  }

  if (payment.walletType === 'click_to_pay') {
    return (
      <span className="flex items-center gap-jade-50">
        <ClickToPayLogoNoBrandIllustration />
        {payment.brand && (
          <>
            <Text color="medium">|</Text>
            <Brand size="small" use={payment.brand.toLowerCase() as CardId} />
          </>
        )}
      </span>
    );
  }

  return WALLET_LOGOS[payment.walletType];
}

function hasInstallmentsField(
  payment: PaymentInfo
): payment is PaymentInfo & { installments: number } {
  return (
    PAYMENT_METHODS_WITH_INSTALLMENTS.includes(payment.paymentMethod) &&
    'installments' in payment
  );
}

function buildContainerClassName(
  variant: 'inline' | 'card',
  className?: string
) {
  return [CONTAINER_BASE, variant === 'card' ? CONTAINER_CARD : '', className]
    .filter(Boolean)
    .join(' ');
}

function buildFields(
  payment: PaymentInfo,
  formatMessage: IntlShape['formatMessage']
): PaymentField[] {
  const fields: PaymentField[] = [
    {
      label: formatMessage({ id: 'paymentInfoDisplay.field.method' }),
      value: formatPaymentMethodName(payment, formatMessage),
      icon: getPaymentMethodIcon(payment),
    },
  ];

  if (hasInstallmentsField(payment)) {
    fields.push({
      label: formatMessage({ id: 'paymentInfoDisplay.field.installments' }),
      value: formatInstallment(payment.installments),
    });
  }

  fields.push(
    {
      label: formatMessage({ id: 'paymentInfoDisplay.field.name' }),
      value: formatCustomerName(payment.customerName),
    },
    {
      label: formatMessage({ id: 'paymentInfoDisplay.field.document' }),
      value: formatDocumentMasked(payment.customerDocument),
    }
  );

  return fields;
}

export function PaymentInfoDisplay({
  payment,
  className,
  variant = 'card',
}: PaymentInfoDisplayProps) {
  const { formatMessage } = useIntl();

  const fields = buildFields(payment, formatMessage);
  const containerClassName = buildContainerClassName(variant, className);

  return (
    <div
      className={containerClassName}
      role="region"
      aria-label={formatMessage({ id: 'paymentInfoDisplay.ariaLabel' })}
    >
      <div className="pt-jade-150 px-jade-100 pb-jade-100">
        <Text variant="heading-xsmall" color="high">
          {formatMessage({ id: 'paymentInfoDisplay.title' })}
        </Text>
      </div>
      <div className="flex flex-col">
        {fields.map((field, index) => (
          <React.Fragment key={field.label}>
            <div className="flex flex-row justify-between items-center gap-jade-150 py-jade-50 pr-jade-100 pl-jade-100 min-h-[40px]">
              <label className="flex-none min-w-[100px]">
                <Text weight="medium" color="medium">
                  {field.label}
                </Text>
              </label>
              <div className="flex items-center justify-end gap-jade-50 flex-1 pl-jade-150 pr-jade-50 break-words">
                {field.icon && (
                  <span
                    className="flex-none inline-flex items-center"
                    aria-hidden="true"
                  >
                    {field.icon}
                  </span>
                )}
                <Text weight="semibold">{field.value}</Text>
              </div>
            </div>
            {index < fields.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
